import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { applicationFilterEmbeds, applyApplicationFilters } from "@/services/applicationFilters"
import { readPersistedApplicationFilters } from "@/utils/applicationFilterState"

interface AdjacentApplications {
  previousApplicationId: string | null
  nextApplicationId: string | null
}

/**
 * Hook to fetch previous and next application IDs for navigation
 * within the same stage, ordered by changed_stage_at timestamp
 */
export const useAdjacentApplications = (
  applicationId: string | undefined,
  currentStage: string | undefined | null,
) => {
  // Read persisted filters from localStorage (kept in sync by Applications page)
  const { stage: _stage, ...adjacentFilters } = readPersistedApplicationFilters()

  return useQuery({
    queryKey: [
      "adjacentApplications",
      applicationId,
      currentStage,
      JSON.stringify(adjacentFilters),
    ],
    queryFn: async (): Promise<AdjacentApplications> => {
      if (!applicationId || !currentStage) {
        return {
          previousApplicationId: null,
          nextApplicationId: null,
        }
      }

      try {
        // First, get the current application's changed_stage_at timestamp
        const { data: currentApp, error: currentAppError } = await supabase
          .from("applications")
          .select("changed_stage_at")
          .eq("id", applicationId)
          .single()

        if (currentAppError || !currentApp) {
          console.error("Error fetching current application:", currentAppError)
          return {
            previousApplicationId: null,
            nextApplicationId: null,
          }
        }

        const changedStageAt = currentApp.changed_stage_at

        const selectColumns: string = `${applicationFilterEmbeds(adjacentFilters)} id`

        // Fetch the previous application (older timestamp)
        let prevQuery = supabase
          .from("applications")
          .select(selectColumns)
          .eq("current_stage", currentStage)
          .lt("changed_stage_at", changedStageAt)
          .order("changed_stage_at", { ascending: false })
          .limit(1)
        // Apply filters inline
        prevQuery = applyApplicationFilters(prevQuery, adjacentFilters)
        const { data: prevData, error: prevError } = await prevQuery

        if (prevError) {
          console.error("Error fetching previous application:", prevError)
        }

        // Fetch the next application (newer timestamp)
        let nextQuery = supabase
          .from("applications")
          .select(selectColumns)
          .eq("current_stage", currentStage)
          .gt("changed_stage_at", changedStageAt)
          .order("changed_stage_at", { ascending: true })
          .limit(1)
        // Apply filters inline
        nextQuery = applyApplicationFilters(nextQuery, adjacentFilters)
        const { data: nextData, error: nextError } = await nextQuery

        if (nextError) {
          console.error("Error fetching next application:", nextError)
        }

        return {
          previousApplicationId: (prevData as unknown as { id: string }[])?.[0]?.id ?? null,
          nextApplicationId: (nextData as unknown as { id: string }[])?.[0]?.id ?? null,
        }
      } catch (error) {
        console.error("Error in useAdjacentApplications:", error)
        return {
          previousApplicationId: null,
          nextApplicationId: null,
        }
      }
    },
    enabled: !!applicationId && !!currentStage,
  })
}
